import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import billiardsPic from '../assets/storyPics/billards.jpg';
import collegePic from '../assets/storyPics/college.jpeg';
import graduationPic from '../assets/storyPics/graduation.jpg';
import storyPic from '../assets/storyPics/storyPic.jpg';
import snowPic from '../assets/storyPics/snow.jpg';
import chanukahPic from '../assets/storyPics/chanuka.jpeg';
import gamePic from '../assets/storyPics/game.jpeg';
import floorPic from '../assets/storyPics/floor.jpg';
import engagementPic from '../assets/storyPics/engagement.jpeg';
import caliPic from '../assets/storyPics/cali.jpeg';
import fireworksPic from '../assets/storyPics/fireworks.jpeg';
import kingConePic from '../assets/storyPics/kingCone.jpeg';
import suitPic from '../assets/storyPics/suit.jpeg';
import stickPic from '../assets/storyPics/stick.jpeg';
import picnicPic from '../assets/storyPics/picnic.jpeg';
import multiPic from '../assets/storyPics/multi.jpeg';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 3rem;
  color: #333;
  font-family: 'Lora', serif;
  font-weight: 500;
`;

const MidTitle = styled.h1`
  text-align: center;
  margin-bottom: 3rem;
  margin-top: 3rem;
  color: #333;
  font-family: 'Lora', serif;
  font-weight: 500;
`;

const Timeline = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  
  &::after {
    content: '';
    position: absolute;
    width: 2px;
    background-color: #333;
    top: 0;
    bottom: 0;
    left: 50%;
    margin-left: -1px;
    
    @media (max-width: 768px) {
      left: 31px;
    }
  }
`;

const TimelineItem = styled.div`
  padding: 10px 40px;
  position: relative;
  width: 50%;
  left: ${props => props.position === 'right' ? '50%' : '0'};
  
  @media (max-width: 768px) {
    width: 100%;
    padding-left: 70px;
    padding-right: 25px;
    left: 0;
  }
`;

const TimelineContent = styled.div`
  padding: 20px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
  font-family: 'EB Garamond', serif;

  p {
    font-family: 'EB Garamond', serif;
    font-size: 1.2rem;
    line-height: 1.6;
    margin-top: 1rem;
    color: #333;
  }
`;

const Date = styled.span`
  font-weight: 600;
  color: #666;
  font-family: 'Lora', serif;
  font-size: 1.2rem;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 10px 0;
  background-color: #f0f0f0;
  overflow: hidden;
  border-radius: 4px;
  min-height: 200px;
  transform: translateZ(0);
  will-change: transform;
`;

const StoryImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  cursor: pointer;
  object-fit: contain;
  transition: transform 0.2s, opacity 0.3s ease-in-out;
  opacity: ${props => props.isLoaded ? 1 : 0};
  transform: scale(${props => props.isLoaded ? 1 : 1.05});
  will-change: transform, opacity;

  &:hover {
    transform: scale(${props => props.isLoaded ? 1.02 : 1.05});
  }
`;

const ImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  min-height: 200px;
  background-color: #f0f0f0;
  display: ${props => props.isHidden ? 'none' : 'block'};
  transform: translateZ(0);
`;

const TimelineImage = ({ src, alt, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imageRef = React.useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.1
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  return (
    <ImageContainer ref={imageRef}>
      <ImagePlaceholder isHidden={isLoaded} />
      {isVisible && (
        <StoryImage
          src={src}
          alt={alt}
          onClick={onClick}
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
          isLoaded={isLoaded}
        />
      )}
    </ImageContainer>
  );
};

const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
`;

const LightboxImage = styled.img`
  max-width: 90%;
  max-height: 90vh;
  object-fit: contain;
  transform: translateZ(0);
  will-change: transform;
`;

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  z-index: 1001;
  font-family: 'EB Garamond', serif;
`;

const OurStory = () => {
  const [lightbox, setLightbox] = useState({ isOpen: false, image: null });

  const openLightbox = (image) => {
    setLightbox({ isOpen: true, image });
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightbox({ isOpen: false, image: null });
    document.body.style.overflow = 'auto';
  };

  return (
    <PageContainer>
      <Title>Our Story</Title>
      <Timeline>
        <TimelineItem position="left">
          <TimelineContent>
            <Date>December 2018</Date>
            <TimelineImage 
              src={billiardsPic} 
              alt="How we met" 
              onClick={() => openLightbox(billiardsPic)}
            />
            <p>After David caught Hana's attention with his masterful Charades gameplay at the Zemser's, the two joined their friends on an unofficial first date at Billiards.</p>
          </TimelineContent>
        </TimelineItem>
        
        <TimelineItem position="right">
          <TimelineContent>
            <Date>May 2019</Date>
            <TimelineImage 
              src={collegePic} 
              alt="Visiting Jared" 
              onClick={() => openLightbox(collegePic)}
            />
            <p>David "visited Jared" at Cornell University and was Hana's date to her final SDT formal.</p>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem position="left">
          <TimelineContent>
            <Date>June 2019</Date>
            <TimelineImage 
              src={caliPic} 
              alt="Birthright" 
              onClick={() => openLightbox(caliPic)}
            />
            <p>Hana and David then went on the same post-graduation Birthright trip with their friends.</p>
          </TimelineContent>
        </TimelineItem>
        
        <TimelineItem position="right">
          <TimelineContent>
            <Date>August 2019</Date>
            <TimelineImage 
              src={storyPic} 
              alt="Visiting Boston" 
              onClick={() => openLightbox(storyPic)}
            />
            <p>Hana moved to Boston and David came to visit often.</p>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem position="left">
          <TimelineContent>
            <Date>January 2020</Date>
            <TimelineImage 
              src={snowPic} 
              alt="First Vacation" 
              onClick={() => openLightbox(snowPic)}
            />
            <p>Hana and David took their first road trip to Burlington, VT</p>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem position="right">
          <TimelineContent>
            <Date>July 2020</Date>
            <TimelineImage 
              src={kingConePic} 
              alt="King Cone" 
              onClick={() => openLightbox(kingConePic)}
            />
            <p>Pandemic activities at the best ice cream shoppe</p>
          </TimelineContent>
        </TimelineItem>
        
        <TimelineItem position="left">
          <TimelineContent>
            <Date>May 2021</Date>
            <TimelineImage 
              src={graduationPic} 
              alt="Graduating" 
              onClick={() => openLightbox(graduationPic)}
            />
            <p>Hana graduated with her MPH and moved back to NYC. She allegedly promised this would be her last degree.</p>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem position="right">
          <TimelineContent>
            <Date>July 2021</Date>
            <TimelineImage 
              src={fireworksPic} 
              alt="Graduating" 
              onClick={() => openLightbox(fireworksPic)}
            />
            <p>Hana and David are finally living in the same city.</p>
          </TimelineContent>
        </TimelineItem>
      </Timeline>
      <MidTitle>And have been making memories ever since!</MidTitle>
      <Timeline>
        <TimelineItem position="left">
          <TimelineContent>
            <TimelineImage 
              src={gamePic} 
              alt="Bethlehem, PA" 
              onClick={() => openLightbox(gamePic)}
            />
            <p>Bethlehem, PA</p>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem position="right">
          <TimelineContent>
            <TimelineImage 
              src={multiPic} 
              alt="Cancun, Mexico" 
              onClick={() => openLightbox(multiPic)}
            />
            <p>Cancun, Mexico</p>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem position="left">
          <TimelineContent>
            <TimelineImage 
              src={picnicPic} 
              alt="Plattsburgh, NY" 
              onClick={() => openLightbox(picnicPic)}
            />
            <p>Plattsburgh, NY</p>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem position="right">
          <TimelineContent>
            <TimelineImage 
              src={suitPic} 
              alt="Durham, NC" 
              onClick={() => openLightbox(suitPic)}
            />
            <p>Durham, NC</p>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem position="left">
          <TimelineContent>
            <TimelineImage 
              src={chanukahPic} 
              alt="Chanukah" 
              onClick={() => openLightbox(chanukahPic)}
            />
            <p>Spending Chanukah together with Po.</p>
          </TimelineContent>
        </TimelineItem>

        <TimelineItem position="right">
          <TimelineContent>
            <TimelineImage 
              src={stickPic} 
              alt="Norwalk, CT" 
              onClick={() => openLightbox(stickPic)}
            />
            <p>Norwalk, CT</p>
          </TimelineContent>
        </TimelineItem>
        
        <TimelineItem position="left">
          <TimelineContent>
            <Date>August 2024</Date>
            <TimelineImage 
              src={floorPic} 
              alt="Roomates" 
              onClick={() => openLightbox(floorPic)}
            />
            <p>Roomates!</p>
          </TimelineContent>
        </TimelineItem>
        
        <TimelineItem position="right">
          <TimelineContent>
            <Date>March 30, 2025</Date>
            <TimelineImage 
              src={engagementPic} 
              alt="Engaged!" 
              onClick={() => openLightbox(engagementPic)}
            />
            <p>Engaged!</p>
          </TimelineContent>
        </TimelineItem>
      </Timeline>

      {lightbox.isOpen && (
        <LightboxOverlay onClick={closeLightbox}>
          <CloseButton onClick={closeLightbox}>×</CloseButton>
          <LightboxImage 
            src={lightbox.image} 
            alt="Enlarged view" 
            onClick={(e) => e.stopPropagation()}
          />
        </LightboxOverlay>
      )}
    </PageContainer>
  );
};

export default OurStory; 